import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.png";

export default function Login() {
  const { login } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await api.post("/api/auth/login", {
        email,
        password,
      });

      login(res.data.user, res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || "System access denied");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.gridOverlay} />
      <Box sx={styles.glowOrb} />

      <Paper sx={styles.card(isMobile, isTablet)}>
        {/* LEFT PANEL (hidden on mobile) */}
        {!isMobile && (
          <Box sx={styles.left}>
            <Box sx={styles.brandTop}>
              <img src={logo} alt="SFVTECH" style={{ width: 70 }} />
              <Typography sx={styles.brandName}>SFV TECH</Typography>
            </Box>

            <Typography sx={styles.tagline}>
              Engineering • Automation • AI Systems
            </Typography>

            <Typography sx={styles.subtext}>
              Secure Enterprise Access Gateway
            </Typography>
          </Box>
        )}

        {/* RIGHT PANEL */}
        <Box sx={styles.right(isMobile)}>
          {/* MOBILE HEADER */}
          {isMobile && (
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img src={logo} alt="SFVTECH" style={{ width: 55 }} />
              <Typography sx={{ fontWeight: 800, mt: 1 }}>
                SFV TECH
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
                Secure Access Portal
              </Typography>
            </Box>
          )}

          <Typography sx={styles.title}>
            System Authentication
          </Typography>

          <Typography sx={styles.subtitle}>
            Enter authorized credentials to continue
          </Typography>

          <form onSubmit={handleLogin}>
            {/* EMAIL — FIXED (NO OVERLAP GUARANTEE) */}
            <TextField
              fullWidth
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              sx={styles.input}
            />

            {/* PASSWORD — FIXED (NO OVERLAP GUARANTEE) */}
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ ...styles.input, mt: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* ERROR */}
            {error && <Box sx={styles.error}>{error}</Box>}

            {/* BUTTON */}
            <Button
              fullWidth
              type="submit"
              disabled={loading}
              sx={styles.button}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "ACCESS SYSTEM"
              )}
            </Button>

            <Typography sx={styles.copy}>
              © {new Date().getFullYear()} SFV TECH — Enterprise Platform
            </Typography>
          </form>
        </Box>
      </Paper>
    </Box>
  );
}

/* ================= STYLES ================= */

const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "radial-gradient(circle at top, #0b1b33, #050a14, #02040a)",
    position: "relative",
    overflow: "hidden",
    px: 2,
  },

  gridOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
    backgroundSize: "50px 50px",
    opacity: 0.15,
  },

  glowOrb: {
    position: "absolute",
    width: 450,
    height: 450,
    background: "rgba(0,170,255,0.18)",
    filter: "blur(130px)",
    borderRadius: "50%",
    top: "10%",
    left: "15%",
  },

  /* RESPONSIVE CARD */
  card: (isMobile, isTablet) => ({
    width: isMobile ? "95%" : isTablet ? "90%" : "1000px",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    borderRadius: 4,
    overflow: "hidden",
    background: "rgba(10, 18, 35, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.08)",
  }),

  /* LEFT PANEL */
  left: {
    flex: 1,
    p: 5,
    color: "#fff",
    background:
      "linear-gradient(135deg, #071428 0%, #0a1f3d 50%, #08162d 100%)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  brandTop: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },

  brandName: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: 2,
  },

  tagline: {
    opacity: 0.85,
    fontSize: 14,
    mb: 2,
  },

  subtext: {
    opacity: 0.5,
    fontSize: 12,
  },

  /* RIGHT PANEL */
  right: (isMobile) => ({
    flex: 1,
    p: isMobile ? 3 : 5,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }),

  title: {
    fontSize: 22,
    fontWeight: 700,
    mb: 1,
  },

  subtitle: {
    opacity: 0.6,
    fontSize: 13,
    mb: 3,
  },

  /* 🔥 FIXED INPUT SYSTEM (NO OVERLAP POSSIBLE) */
  input: {
    mt: 1,
    input: {
      color: "#fff",
    },
    label: {
      color: "rgba(255,255,255,0.6)",
    },
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.03)",
      borderRadius: 2,

      "& fieldset": {
        borderColor: "rgba(255,255,255,0.15)",
      },

      "&:hover fieldset": {
        borderColor: "#00aaff55",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#00aaff",
      },
    },
  },

  button: {
    mt: 3,
    py: 1.3,
    fontWeight: 700,
    background: "linear-gradient(90deg,#00aaff,#0057ff)",
    color: "#fff",
    borderRadius: 2,
    "&:hover": { opacity: 0.9 },
  },

  error: {
    mt: 2,
    color: "#ff4d4f",
    fontSize: 13,
  },

  copy: {
    mt: 3,
    textAlign: "center",
    opacity: 0.4,
    fontSize: 11,
  },
};